import type { StockItem, StockUsage } from '../../domain/fulfillment';
import type { Item, Part } from '../../domain/inventory';
import { humanizeIdentifier } from '../../lib/formatting';
import {
  generationsOfType,
  partIdsOfGeneration,
  partIdsOfType,
  type PartGeneration,
} from './part-choice';
import { matchesStockSearch } from './stock-item-search';

/**
 * What an order line is for: a serialized part type or a stock item. People
 * choose either from one list; the line's requirement type follows from it.
 */
export type LineChoice =
  { kind: 'serialized'; partType: string } | { kind: 'stock'; stockItemId: string };

/** The list's filter: who a stock item is for, or serialized equipment. */
export type LineFilter = '' | StockUsage | 'SERIALIZED';

export interface PartTypeEntry {
  kind: 'serialized';
  key: string;
  type: string;
  name: string;
  /** Builds of the type, newest first. */
  partIds: string[];
  /** Items of the type in warehouse storage; undefined while loading. */
  inStorage: number | undefined;
  /** In storage by generation, for types with both. */
  byGeneration: { generation: PartGeneration; count: number | undefined }[];
}

export interface StockEntry {
  kind: 'stock';
  key: string;
  item: StockItem;
}

export type LineEntry = PartTypeEntry | StockEntry;

export function lineChoiceKey(choice: LineChoice): string {
  return choice.kind === 'serialized' ? `part:${choice.partType}` : `stock:${choice.stockItemId}`;
}

export function entryChoice(entry: LineEntry): LineChoice {
  return entry.kind === 'serialized'
    ? { kind: 'serialized', partType: entry.type }
    : { kind: 'stock', stockItemId: entry.item.id };
}

export function entryName(entry: LineEntry): string {
  return entry.kind === 'serialized' ? entry.name : entry.item.displayName;
}

/**
 * Serialized items ready to pick, by part ID: those in warehouse storage.
 * One ready to ship is already picked for an order.
 */
export function inStorageByPart(items: readonly Item[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    if (item.status !== 'IN_WAREHOUSE_STORAGE') continue;
    counts.set(item.partId, (counts.get(item.partId) ?? 0) + 1);
  }
  return counts;
}

/** One entry per part type that has builds. */
export function partTypeEntries(
  parts: readonly Part[],
  inStorage: ReadonlyMap<string, number> | undefined,
): PartTypeEntry[] {
  const count = (partIds: readonly string[]) =>
    inStorage ? partIds.reduce((sum, id) => sum + (inStorage.get(id) ?? 0), 0) : undefined;
  const types = [...new Set(parts.map((part) => part.type))].filter(
    (type) => type !== 'PART_TYPE_UNSPECIFIED',
  );
  return types.map((type) => {
    const partIds = partIdsOfType(parts, type);
    const generations = generationsOfType(parts, type);
    return {
      kind: 'serialized',
      key: `part:${type}`,
      type,
      name: humanizeIdentifier(type),
      partIds,
      inStorage: count(partIds),
      byGeneration:
        generations.length > 1
          ? generations.map((generation) => ({
              generation,
              count: count(partIdsOfGeneration(parts, type, generation)),
            }))
          : [],
    };
  });
}

/**
 * True when every word of the query appears in the type's name or code, or
 * one of its builds or part IDs: "winch vvdn" finds Winch system.
 */
export function matchesPartTypeSearch(
  entry: PartTypeEntry,
  parts: readonly Part[],
  query: string,
): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const builds = parts.filter((part) => part.type === entry.type).map((part) => part.build);
  const haystack = [entry.name, entry.type, ...entry.partIds, ...builds].join(' ').toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/** The entries a filter, the available-only box and a search leave, by name. */
export function filterLineEntries(
  entries: readonly LineEntry[],
  {
    filter,
    availableOnly,
    query,
    parts,
    stockAvailable,
  }: {
    filter: LineFilter;
    availableOnly: boolean;
    query: string;
    parts: readonly Part[];
    stockAvailable: (id: string) => number | undefined;
  },
): LineEntry[] {
  return entries
    .filter((entry) => {
      if (entry.kind === 'serialized') {
        if (filter !== '' && filter !== 'SERIALIZED') return false;
        if (availableOnly && (entry.inStorage ?? 0) <= 0) return false;
        return matchesPartTypeSearch(entry, parts, query);
      }
      if (filter === 'SERIALIZED' || (filter !== '' && entry.item.usage !== filter)) return false;
      if (availableOnly && (stockAvailable(entry.item.id) ?? 0) <= 0) return false;
      return matchesStockSearch(entry.item, query);
    })
    .sort((a, b) => entryName(a).localeCompare(entryName(b)));
}
